import React, { useMemo, useState } from 'react';
import { Field, Input, Select, Button, Badge, Row, SaveBar, uid } from './ui';
import useDraft from './useDraft';
import { autoWinnerFor, winnersOf } from '../lib/standings';
import { formatDate, todayIso, byDateDesc, teamLabel } from '../lib/format';

const GAME_NAMES = ['Trešeta', 'Briškula', 'Bela', 'Bela trešeta'];
const STATUSES = ['KRAJ', 'NAPETO', 'UTJEŠNA', 'ŠETNJA'];

/**
 * Three-state side picker: every player is either unused, on team A, or on
 * team B. Because names come from the roster rather than free text, a player
 * can no longer be silently split in two by a typo.
 */
function SidePicker({ roster, teamA, teamB, onChange }) {
  const sideOf = (name) => (teamA.includes(name) ? 'A' : teamB.includes(name) ? 'B' : null);

  const setSide = (name, side) => {
    const a = teamA.filter((n) => n !== name);
    const b = teamB.filter((n) => n !== name);
    if (side === 'A') a.push(name);
    if (side === 'B') b.push(name);
    onChange(a, b);
  };

  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
      {roster.map((name) => {
        const side = sideOf(name);
        return (
          <div key={name} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5">
            <span className={`text-sm font-medium ${side ? 'text-slate-900' : 'text-slate-400'}`}>{name}</span>
            <div className="flex overflow-hidden rounded border border-slate-300">
              {[['A', '1'], ['B', '2'], [null, '–']].map(([val, label]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setSide(name, side === val ? null : val)}
                  className={`w-8 py-0.5 text-xs font-bold transition ${
                    side === val
                      ? val === 'A' ? 'bg-[#009FE3] text-white' : val === 'B' ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-600'
                      : 'bg-white text-slate-400 hover:bg-slate-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WinnerBox({ match, sport, onChange }) {
  const auto = autoWinnerFor(match, sport);
  const effective = winnersOf(match, sport);
  const manual = match.autoWinner === false;
  const tie = auto.length === 0;

  const pick = (side) => {
    const team = side === 'A' ? match.teamA : match.teamB;
    onChange({ ...match, autoWinner: false, pointsFor: [...team] });
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Pobjednik</span>
        {effective.length > 0
          ? <Badge tone={manual ? 'amber' : 'green'}>{teamLabel(effective)}</Badge>
          : <Badge tone="red">neodlučeno</Badge>}
        {manual ? <Badge tone="amber">ručno</Badge> : <Badge tone="blue">automatski</Badge>}
      </div>

      {tie && !manual && (
        <p className="mb-2 text-xs text-red-600">
          Rezultat je neodlučen pa se pobjednik ne može izračunati. Odaberi ručno.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={manual}
            onChange={(e) => onChange(
              e.target.checked
                ? { ...match, autoWinner: false, pointsFor: effective }
                : { ...match, autoWinner: true, pointsFor: auto }
            )}
          />
          Ručno postavi pobjednika
        </label>
        {manual && (
          <>
            <Button className="!py-1" onClick={() => pick('A')}>{teamLabel(match.teamA) || 'Ekipa 1'}</Button>
            <Button className="!py-1" onClick={() => pick('B')}>{teamLabel(match.teamB) || 'Ekipa 2'}</Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function MatchesEditor({ sport, stored, roster }) {
  const section = sport === 'karte' ? 'karte' : 'boce';
  const { draft, setDraft, dirty, saving, error, save, reset } = useDraft(section, stored);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('');

  const sorted = useMemo(() => [...draft].sort(byDateDesc), [draft]);
  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((m) =>
      [m.date, ...(m.teamA || []), ...(m.teamB || []), m.note].join(' ').toLowerCase().includes(q));
  }, [sorted, filter]);

  const patch = (id, next) => setDraft((d) => d.map((m) => (m.id === id ? next : m)));
  const remove = (id) => {
    if (!window.confirm('Obrisati ovaj meč?')) return;
    setDraft((d) => d.filter((m) => m.id !== id));
  };

  const addMatch = () => {
    const lastDate = sorted[0]?.date || todayIso();
    const base = { id: uid(sport === 'karte' ? 'k' : 'b'), date: lastDate, teamA: [], teamB: [], pointsFor: [], autoWinner: true };
    const match = sport === 'karte'
      ? { ...base, games: [{ name: 'Trešeta', a: 0, b: 0 }] }
      : { ...base, scoreA: 0, scoreB: 0, status: 'KRAJ', note: '' };
    setDraft((d) => [match, ...d]);
    setOpenId(match.id);
  };

  // keep pointsFor in sync whenever a score or line-up changes on an auto match
  const withAuto = (m) => (m.autoWinner === false ? m : { ...m, pointsFor: autoWinnerFor(m, sport) });

  const summaryOf = (m) => {
    const a = teamLabel(m.teamA, ', ') || '—';
    const b = teamLabel(m.teamB, ', ') || '—';
    const score = sport === 'karte'
      ? (m.games || []).map((g) => `${g.a}:${g.b}`).join('  ')
      : `${m.scoreA} : ${m.scoreB}`;
    return `${a}  vs  ${b}${score ? `   ·   ${score}` : ''}`;
  };

  const problems = (m) => {
    const out = [];
    if (!m.date) out.push('nema datuma');
    if (m.teamA.length === 0 || m.teamB.length === 0) out.push('nepotpune ekipe');
    if (sport === 'karte' && (m.teamA.length !== 2 || m.teamB.length !== 2)) out.push('karte su 2 na 2');
    if (winnersOf(m, sport).length === 0) out.push('nema pobjednika');
    return out;
  };

  const brokenCount = draft.filter((m) => problems(m).length > 0).length;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button onClick={addMatch}>+ Ručni unos</Button>
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Traži po igraču ili datumu…"
          className="max-w-xs"
        />
        <span className="text-xs text-slate-500">{visible.length} od {draft.length}</span>
        {brokenCount > 0 && <Badge tone="red">{brokenCount} s greškom</Badge>}
      </div>

      <div className="space-y-2">
        {visible.map((m) => {
          const issues = problems(m);
          const winner = winnersOf(m, sport);
          return (
            <Row
              key={m.id}
              open={openId === m.id}
              onToggle={() => setOpenId(openId === m.id ? null : m.id)}
              summary={summaryOf(m)}
              meta={
                <>
                  <Badge>{formatDate(m.date)}</Badge>
                  {winner.length > 0 && <Badge tone="green">{teamLabel(winner)}</Badge>}
                  {m.autoWinner === false && <Badge tone="amber">ručno</Badge>}
                  {issues.map((i) => <Badge key={i} tone="red">{i}</Badge>)}
                </>
              }
              onDelete={() => remove(m.id)}
            >
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Datum">
                    <Input type="date" value={m.date} onChange={(e) => patch(m.id, withAuto({ ...m, date: e.target.value }))} />
                  </Field>
                  {sport === 'boce' && (
                    <Field label="Status">
                      <Select value={m.status} onChange={(e) => patch(m.id, { ...m, status: e.target.value })}>
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </Select>
                    </Field>
                  )}
                </div>

                <Field
                  label={`Ekipe — 1: ${teamLabel(m.teamA, ', ') || '…'}  ·  2: ${teamLabel(m.teamB, ', ') || '…'}`}
                  hint={sport === 'karte' ? 'Karte se igraju 2 na 2.' : 'Boće mogu biti 1 na 1, 2 na 2, 3 na 3 — kako ste igrali.'}
                >
                  <SidePicker
                    roster={roster}
                    teamA={m.teamA}
                    teamB={m.teamB}
                    onChange={(a, b) => patch(m.id, withAuto({ ...m, teamA: a, teamB: b }))}
                  />
                </Field>

                {sport === 'karte' ? (
                  <Field label="Partije">
                    <div className="space-y-2">
                      {(m.games || []).map((g, gi) => (
                        <div key={gi} className="flex items-center gap-2">
                          <Select
                            value={g.name}
                            onChange={(e) => {
                              const games = m.games.map((x, i) => (i === gi ? { ...x, name: e.target.value } : x));
                              patch(m.id, withAuto({ ...m, games }));
                            }}
                            className="max-w-[160px]"
                          >
                            {GAME_NAMES.map((n) => <option key={n}>{n}</option>)}
                          </Select>
                          <Input
                            type="number" value={g.a} className="max-w-[80px] text-center"
                            onChange={(e) => {
                              const games = m.games.map((x, i) => (i === gi ? { ...x, a: Number(e.target.value) } : x));
                              patch(m.id, withAuto({ ...m, games }));
                            }}
                          />
                          <span className="text-slate-400">:</span>
                          <Input
                            type="number" value={g.b} className="max-w-[80px] text-center"
                            onChange={(e) => {
                              const games = m.games.map((x, i) => (i === gi ? { ...x, b: Number(e.target.value) } : x));
                              patch(m.id, withAuto({ ...m, games }));
                            }}
                          />
                          <Button
                            variant="danger" className="!px-2 !py-1"
                            onClick={() => patch(m.id, withAuto({ ...m, games: m.games.filter((_, i) => i !== gi) }))}
                          >✕</Button>
                        </div>
                      ))}
                      <Button
                        onClick={() => patch(m.id, withAuto({
                          ...m,
                          games: [...(m.games || []), { name: GAME_NAMES[(m.games || []).length] || 'Bela', a: 0, b: 0 }],
                        }))}
                      >+ Partija</Button>
                    </div>
                  </Field>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Rezultat (A : B)">
                      <div className="flex items-center gap-2">
                        <Input
                          type="number" value={m.scoreA} className="text-center"
                          onChange={(e) => patch(m.id, withAuto({ ...m, scoreA: Number(e.target.value) }))}
                        />
                        <span className="text-slate-400">:</span>
                        <Input
                          type="number" value={m.scoreB} className="text-center"
                          onChange={(e) => patch(m.id, withAuto({ ...m, scoreB: Number(e.target.value) }))}
                        />
                      </div>
                    </Field>
                    <Field label="Bilješka" hint="Neobavezno — prikazuje se ispod rezultata.">
                      <Input value={m.note || ''} onChange={(e) => patch(m.id, { ...m, note: e.target.value })} />
                    </Field>
                  </div>
                )}

                <WinnerBox match={m} sport={sport} onChange={(next) => patch(m.id, next)} />
              </div>
            </Row>
          );
        })}
      </div>

      {visible.length === 0 && (
        <p className="py-8 text-center text-sm text-slate-400">Nema mečeva za prikaz.</p>
      )}

      <SaveBar dirty={dirty} saving={saving} error={error} onSave={save} onReset={reset} count={draft.length} />
    </div>
  );
}

